// ═══════════════════════════════════════════════
//   STAFF SPRINT — minigame
// ═══════════════════════════════════════════════
//
// Objective:   Navigate the staff member through the konbini maze to find the
//              exit booth, collecting coins and merch while outrunning angry customers.
// Type:        maze
// Description: Arrow keys / WASD steer the staff member (hold to keep moving);
//              swipe on mobile. Collect 🪙 coins (+1) and merch 🎀 (+3). Reach
//              the exit 🏪 for +5 and a new faster maze. Touch an angry customer
//              costs 1 HP (3 total). Enemies chase via BFS; speed ramps each level.
// Tunables:    GAME_MAZE in data.js.

const {S, canvas, ctx, claimGameReward, addCleanup, showGameOver, setHP, loseHP} = window;

// ── Module-level runtime state ────────────────────────────────────────
let mazeGrid       = [];
let gridRows       = 0;
let gridCols       = 0;
let playerR        = 0;
let playerC        = 0;
let exitR          = 0;
let exitC          = 0;
let enemies        = [];
let items          = new Map();
let level          = 0;
let invincUntil    = 0;
let lastPlayerMove = 0;
let lastEnemyMove  = 0;
let curEnemyMs     = 600;
let heldDir        = null;
let mazeLastDir    = {dr: 0, dc: 1}; // last direction moved — used for Pac-Man facing
let touchClearId   = null;
let swipeTouchX    = 0;
let swipeTouchY    = 0;

const GHOST_COLORS = ['#ff0000', '#ffb8de', '#00e0e0', '#ffb852'];

// ── Maze generation — iterative backtracking on doubled grid ──────────
function mazeShuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

function buildMaze(rows, cols) {
    const g = Array.from({length: rows}, () => new Uint8Array(cols).fill(1));
    const stack = [[1, 1]];
    g[1][1] = 0;
    while (stack.length) {
        const [r, c] = stack[stack.length - 1];
        const nbrs = mazeShuffle([[0,-2],[0,2],[-2,0],[2,0]]).filter(([dr, dc]) => {
            const nr = r + dr, nc = c + dc;
            return nr > 0 && nr < rows - 1 && nc > 0 && nc < cols - 1 && g[nr][nc] === 1;
        });
        if (!nbrs.length) { stack.pop(); continue; }
        const [dr, dc] = nbrs[0];
        g[r + dr/2][c + dc/2] = 0;
        g[r + dr][c + dc] = 0;
        stack.push([r + dr, c + dc]);
    }
    // Remove walls to create loops — higher loopFrac = more alternate routes to the exit
    const mR = (rows - 1) / 2, mC = (cols - 1) / 2;
    const loops = Math.floor(mR * mC * window.GAME_MAZE.loopFrac);
    for (let n = 0; n < loops; n++) {
        if (Math.random() < 0.5 && mR > 1) {
            const r = Math.floor(Math.random() * (mR - 1));
            const c = Math.floor(Math.random() * mC);
            g[r*2+2][c*2+1] = 0;
        } else if (mC > 1) {
            const r = Math.floor(Math.random() * mR);
            const c = Math.floor(Math.random() * (mC - 1));
            g[r*2+1][c*2+2] = 0;
        }
    }
    return g;
}

// ── Random valid step ─────────────────────────────────────────────────
function randomStep(fromR, fromC) {
    const mR = (gridRows - 1) / 2, mC = (gridCols - 1) / 2;
    const dirs = [[0,1],[0,-1],[1,0],[-1,0]].filter(([dr, dc]) => {
        const nr = fromR + dr, nc = fromC + dc;
        return nr >= 0 && nr < mR && nc >= 0 && nc < mC &&
               mazeGrid[fromR*2+1+dr][fromC*2+1+dc] === 0;
    });
    if (!dirs.length) return null;
    const [dr, dc] = dirs[Math.floor(Math.random() * dirs.length)];
    return {dr, dc};
}

// ── BFS shortest-path first step ─────────────────────────────────────
function bfsStep(fromR, fromC, toR, toC) {
    if (fromR === toR && fromC === toC) return null;
    const mR = (gridRows - 1) / 2, mC = (gridCols - 1) / 2;
    const vis = Array.from({length: mR}, () => new Uint8Array(mC));
    const q = [{r: fromR, c: fromC, first: null}];
    vis[fromR][fromC] = 1;
    while (q.length) {
        const {r, c, first} = q.shift();
        for (const [dr, dc] of [[0,1],[0,-1],[1,0],[-1,0]]) {
            const nr = r + dr, nc = c + dc;
            if (nr < 0 || nr >= mR || nc < 0 || nc >= mC || vis[nr][nc]) continue;
            if (mazeGrid[r*2+1+dr][c*2+1+dc] === 1) continue;
            const move = first || {dr, dc};
            if (nr === toR && nc === toC) return move;
            vis[nr][nc] = 1;
            q.push({r: nr, c: nc, first: move});
        }
    }
    return null;
}

// ── Canvas drawing helpers ────────────────────────────────────────────
function drawMazePacman(cx, cy, r, dir, now) {
    const mouthOpen = 0.14 + Math.abs(Math.sin(now / 115)) * 0.19;
    let rot = 0;
    if (dir) {
        if      (dir.dc > 0) rot = 0;
        else if (dir.dc < 0) rot = Math.PI;
        else if (dir.dr > 0) rot = Math.PI / 2;
        else                 rot = -Math.PI / 2;
    }
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rot);
    ctx.fillStyle = '#ffe000';
    ctx.shadowColor = '#ffe000';
    ctx.shadowBlur = 5;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, r, mouthOpen * Math.PI, (2 - mouthOpen) * Math.PI);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.restore();
}

function drawMazeGhost(cx, cy, r, color, frightened, now) {
    const blinkOn = Math.floor(now / 200) % 2 === 0;
    const fc = frightened ? (blinkOn ? '#3333ee' : '#ffccff') : color;

    ctx.fillStyle = fc;
    ctx.beginPath();
    ctx.arc(cx, cy - r * 0.2, r, Math.PI, 0); // dome
    ctx.lineTo(cx + r, cy + r * 0.85);
    // wavy skirt — 3 bumps from right to left
    const waves = 3, step = (r * 2) / waves;
    for (let i = 0; i < waves; i++) {
        const x1 = cx + r - step * (i + 0.5);
        const x2 = cx + r - step * (i + 1);
        ctx.quadraticCurveTo(x1, cy + r * 1.28, x2, cy + r * 0.85);
    }
    ctx.closePath();
    ctx.fill();

    if (!frightened) {
        ctx.fillStyle = 'white';
        ctx.beginPath(); ctx.arc(cx - r*0.34, cy - r*0.22, r*0.21, 0, Math.PI*2); ctx.fill();
        ctx.beginPath(); ctx.arc(cx + r*0.34, cy - r*0.22, r*0.21, 0, Math.PI*2); ctx.fill();
        ctx.fillStyle = '#1a1aee';
        ctx.beginPath(); ctx.arc(cx - r*0.30, cy - r*0.18, r*0.10, 0, Math.PI*2); ctx.fill();
        ctx.beginPath(); ctx.arc(cx + r*0.38, cy - r*0.18, r*0.10, 0, Math.PI*2); ctx.fill();
    } else {
        // scared dots
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.beginPath(); ctx.arc(cx - r*0.30, cy - r*0.15, r*0.10, 0, Math.PI*2); ctx.fill();
        ctx.beginPath(); ctx.arc(cx + r*0.30, cy - r*0.15, r*0.10, 0, Math.PI*2); ctx.fill();
    }
}

// ── Level setup ───────────────────────────────────────────────────────
function nextLevel() {
    const CFG = window.GAME_MAZE;
    const cs  = CFG.cellSize;
    level++;

    gridCols = Math.floor(canvas.width  / cs); if (gridCols % 2 === 0) gridCols--;
    gridRows = Math.floor(canvas.height / cs); if (gridRows % 2 === 0) gridRows--;
    const mR = (gridRows - 1) / 2, mC = (gridCols - 1) / 2;

    mazeGrid = buildMaze(gridRows, gridCols);
    playerR = 0; playerC = 0;
    exitR = mR - 1; exitC = mC - 1;

    const pool = [];
    for (let r = 0; r < mR; r++)
        for (let c = 0; c < mC; c++)
            if (r + c >= Math.floor((mR + mC) / 2) && !(r === exitR && c === exitC))
                pool.push({r, c});
    mazeShuffle(pool);
    enemies = pool.slice(0, CFG.enemiesPerLevel).map(e => ({r: e.r, c: e.c}));

    const occupied = new Set(['0,0', `${exitR},${exitC}`, ...enemies.map(e => `${e.r},${e.c}`)]);
    const spots = [];
    for (let r = 0; r < mR; r++)
        for (let c = 0; c < mC; c++)
            if (!occupied.has(`${r},${c}`)) spots.push({r, c});
    mazeShuffle(spots);
    items.clear();
    let idx = 0;
    for (let i = 0; i < CFG.coinsPerLevel  && idx < spots.length; i++, idx++)
        items.set(`${spots[idx].r},${spots[idx].c}`, {type: 'coin'});
    for (let i = 0; i < CFG.merchPerLevel  && idx < spots.length; i++, idx++)
        items.set(`${spots[idx].r},${spots[idx].c}`, {type: 'merch'});

    curEnemyMs = Math.max(CFG.enemyMoveMin, CFG.enemyMoveStart - (level - 1) * CFG.enemyAccelPerLevel);
    const now = Date.now();
    lastEnemyMove  = now + 800;
    lastPlayerMove = now;
    invincUntil    = 0;
    mazeLastDir    = {dr: 0, dc: 1};

    if (level > 1) toast(`Level ${level}! 👟`);
}

// ── startMaze ─────────────────────────────────────────────────────────
function startMaze() {
    const CFG = window.GAME_MAZE;
    window.gameRunning = true;
    window.gameScore   = 0;
    document.getElementById('mg-score').textContent = '0';
    level = 0;
    heldDir = null;
    mazeLastDir = {dr: 0, dc: 1};
    touchClearId = null;

    setHP(CFG.hp, CFG.hp);
    nextLevel();

    window.gameInterval = setInterval(mazeLoop, 16);
    addCleanup(() => clearInterval(window.gameInterval));
    addCleanup(() => { mazeGrid = []; enemies = []; items.clear(); });

    document.addEventListener('keydown', mazeKeyDown);
    document.addEventListener('keyup',   mazeKeyUp);
    addCleanup(() => {
        document.removeEventListener('keydown', mazeKeyDown);
        document.removeEventListener('keyup',   mazeKeyUp);
    });

    canvas.addEventListener('touchstart', mazeTouchStart, {passive: true});
    canvas.addEventListener('touchend',   mazeTouchEnd,   {passive: true});
    addCleanup(() => {
        canvas.removeEventListener('touchstart', mazeTouchStart);
        canvas.removeEventListener('touchend',   mazeTouchEnd);
        if (touchClearId) { clearTimeout(touchClearId); touchClearId = null; }
    });
}

// ── mazeLoop — runs every 16 ms ───────────────────────────────────────
function mazeLoop() {
    if (!window.gameRunning) return;
    const CFG = window.GAME_MAZE;
    const cs  = CFG.cellSize;
    const now = Date.now();
    const W   = canvas.width, H = canvas.height;
    const mR  = (gridRows - 1) / 2, mC = (gridCols - 1) / 2;

    // Player movement tick
    if (heldDir && now - lastPlayerMove >= CFG.playerMoveMs) {
        lastPlayerMove = now;
        const nr = playerR + heldDir.dr;
        const nc = playerC + heldDir.dc;
        if (nr >= 0 && nr < mR && nc >= 0 && nc < mC &&
            mazeGrid[playerR*2+1 + heldDir.dr][playerC*2+1 + heldDir.dc] === 0) {
            playerR = nr;
            playerC = nc;
            mazeLastDir = heldDir;
        }
        const k = `${playerR},${playerC}`;
        const item = items.get(k);
        if (item) {
            window.gameScore += item.type === 'coin' ? CFG.coinValue : CFG.merchValue;
            document.getElementById('mg-score').textContent = window.gameScore;
            SFX.play('hit');
            items.delete(k);
        }
        if (playerR === exitR && playerC === exitC) {
            window.gameScore += CFG.exitBonus;
            document.getElementById('mg-score').textContent = window.gameScore;
            SFX.play('win');
            nextLevel();
        }
    }

    // Enemy movement tick
    if (now - lastEnemyMove >= curEnemyMs) {
        lastEnemyMove = now;
        for (const e of enemies) {
            const step = Math.random() < CFG.trackChance
                ? bfsStep(e.r, e.c, playerR, playerC)
                : randomStep(e.r, e.c);
            if (step) { e.r += step.dr; e.c += step.dc; }
        }
    }

    // Collision
    if (now >= invincUntil && enemies.some(e => e.r === playerR && e.c === playerC)) {
        invincUntil = now + CFG.invincMs;
        SFX.play('damage');
        if (loseHP() === 0) { endMaze(); return; }
    }

    // ── Render ──────────────────────────────────────────────────────────

    // Background — Pac-Man dark navy
    ctx.fillStyle = '#000020';
    ctx.fillRect(0, 0, W, H);

    // Walls — blue blocks with bevelled edge
    for (let r = 0; r < gridRows; r++) {
        for (let c = 0; c < gridCols; c++) {
            if (mazeGrid[r][c] !== 1) continue;
            ctx.fillStyle = '#1100aa';
            ctx.fillRect(c*cs, r*cs, cs, cs);
            ctx.fillStyle = '#4455ff';
            ctx.fillRect(c*cs + 1, r*cs + 1, cs - 2, 2); // top highlight
            ctx.fillRect(c*cs + 1, r*cs + 1, 2, cs - 2); // left highlight
            ctx.fillStyle = '#080066';
            ctx.fillRect(c*cs + 1, r*cs + cs - 3, cs - 2, 2); // bottom shadow
            ctx.fillRect(c*cs + cs - 3, r*cs + 1, 2, cs - 2); // right shadow
        }
    }

    // Tiny floor dots (decoration — gives the Pac-Man corridor feel)
    ctx.fillStyle = 'rgba(180,180,255,0.13)';
    for (let r = 0; r < mR; r++) {
        for (let c = 0; c < mC; c++) {
            if (items.has(`${r},${c}`)) continue;
            if (r === exitR && c === exitC) continue;
            ctx.beginPath();
            ctx.arc((c*2+1)*cs + cs/2, (r*2+1)*cs + cs/2, 1.4, 0, Math.PI * 2);
            ctx.fill();
        }
    }

    // Exit — gold glow + emoji
    const exX = (exitC*2+1)*cs + cs/2;
    const exY = (exitR*2+1)*cs + cs/2;
    ctx.shadowColor = '#ffd700';
    ctx.shadowBlur = 14;
    ctx.fillStyle = 'rgba(255,215,0,0.20)';
    ctx.beginPath(); ctx.arc(exX, exY, cs * 0.72, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0;
    ctx.font = `${cs - 2}px serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('🏪', exX, exY + 1);

    // Collectibles
    for (const [k, item] of items) {
        const [ir, ic] = k.split(',').map(Number);
        const ix = (ic*2+1)*cs + cs/2;
        const iy = (ir*2+1)*cs + cs/2;
        if (item.type === 'coin') {
            // small pellet
            ctx.fillStyle = '#ffeeaa';
            ctx.shadowColor = '#ffdd44';
            ctx.shadowBlur = 3;
            ctx.beginPath(); ctx.arc(ix, iy, cs * 0.17, 0, Math.PI * 2); ctx.fill();
            ctx.shadowBlur = 0;
        } else {
            // power pellet — blinking large circle
            if (Math.floor(now / 300) % 2 === 0) {
                ctx.fillStyle = 'white';
                ctx.shadowColor = '#ffffff';
                ctx.shadowBlur = 9;
                ctx.beginPath(); ctx.arc(ix, iy, cs * 0.36, 0, Math.PI * 2); ctx.fill();
                ctx.shadowBlur = 0;
            }
        }
    }

    // Enemies (ghosts) — turn blue/blinking when player is invincible
    const frightened = now < invincUntil;
    for (let gi = 0; gi < enemies.length; gi++) {
        const e = enemies[gi];
        drawMazeGhost(
            (e.c*2+1)*cs + cs/2,
            (e.r*2+1)*cs + cs/2,
            cs * 0.44,
            GHOST_COLORS[gi % GHOST_COLORS.length],
            frightened, now
        );
    }

    // Player (Pac-Man) — blinks during invincibility
    if (now >= invincUntil || Math.floor(now / 100) % 2 === 0) {
        drawMazePacman(
            (playerC*2+1)*cs + cs/2,
            (playerR*2+1)*cs + cs/2,
            cs * 0.42, mazeLastDir, now
        );
    }

    // Level badge
    ctx.fillStyle    = 'rgba(255,255,255,0.45)';
    ctx.font         = '10px monospace';
    ctx.textAlign    = 'right';
    ctx.textBaseline = 'top';
    ctx.fillText(`LV ${level}`, W - 3, 3);
}

// ── Input ─────────────────────────────────────────────────────────────
const MAZE_DIR_MAP = {
    ArrowUp:    {dr:-1, dc: 0}, ArrowDown:  {dr: 1, dc: 0},
    ArrowLeft:  {dr: 0, dc:-1}, ArrowRight: {dr: 0, dc: 1},
    w: {dr:-1, dc: 0}, s: {dr: 1, dc: 0},
    a: {dr: 0, dc:-1}, d: {dr: 0, dc: 1},
};

function mazeKeyDown(e) {
    if (!window.gameRunning || S.currentGame !== 'maze') return;
    const dir = MAZE_DIR_MAP[e.key];
    if (!dir) return;
    e.preventDefault();
    heldDir = dir;
}

function mazeKeyUp(e) {
    if (S.currentGame !== 'maze') return;
    const dir = MAZE_DIR_MAP[e.key];
    if (dir && heldDir && dir.dr === heldDir.dr && dir.dc === heldDir.dc) heldDir = null;
}

function mazeTouchStart(e) {
    swipeTouchX = e.touches[0].clientX;
    swipeTouchY = e.touches[0].clientY;
}

function mazeTouchEnd(e) {
    if (!window.gameRunning || S.currentGame !== 'maze') return;
    const dx = e.changedTouches[0].clientX - swipeTouchX;
    const dy = e.changedTouches[0].clientY - swipeTouchY;
    if (Math.abs(dx) < window.GAME_MAZE.swipeThreshold && Math.abs(dy) < window.GAME_MAZE.swipeThreshold) return;
    heldDir = Math.abs(dx) > Math.abs(dy)
        ? (dx > 0 ? {dr:0, dc:1} : {dr:0, dc:-1})
        : (dy > 0 ? {dr:1, dc:0} : {dr:-1, dc:0});
    if (touchClearId) clearTimeout(touchClearId);
    touchClearId = setTimeout(() => { heldDir = null; touchClearId = null; }, window.GAME_MAZE.playerMoveMs + 60);
}

// ── endMaze ───────────────────────────────────────────────────────────
function endMaze() {
    const CFG = window.GAME_MAZE;
    window.gameRunning = false;
    SFX.play(window.gameScore > 0 ? 'win' : 'gameover');
    if (window.gameScore >= CFG.nickThreshold) window.unlockNick('boothfinder');
    const p = Math.min(CFG.rewardMax, window.gameScore * CFG.rewardPer);
    showGameOver(claimGameReward('maze', window.gameScore, p));
}

window.startMaze      = startMaze;
window.mazeLoop       = mazeLoop;
window.endMaze        = endMaze;
window.mazeKeyDown    = mazeKeyDown;
window.mazeKeyUp      = mazeKeyUp;
window.mazeTouchStart = mazeTouchStart;
window.mazeTouchEnd   = mazeTouchEnd;
